import React from 'react';
import { Button } from './Button';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenPrivacyModal: () => void;
  onOpenCookieModal: () => void;
  subject: string;
}

const euCountries = [
  "Alemania", "Austria", "Bélgica", "Bulgaria", "Chipre", "Croacia", "Dinamarca",
  "Eslovaquia", "Eslovenia", "España", "Estonia", "Finlandia", "Francia",
  "Grecia", "Hungría", "Irlanda", "Italia", "Letonia", "Lituania", "Luxemburgo",
  "Malta", "Países Bajos", "Polonia", "Portugal", "República Checa", "Rumanía", "Suecia"
];

const preferredLanguages = [
  "Español", "Inglés", "Francés", "Alemán", "Italiano", "Portugués", "Otro"
];

const inquiryTypes = [
  "Consulta General", "Multa de Tráfico", "Multa de Aparcamiento", "Multa de Velocidad",
  "Alcoholemia", "Informe Pericial", "Representación Judicial", "Otro"
];

export const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, onOpenPrivacyModal, onOpenCookieModal, subject }) => {
  if (!isOpen) return null;

  const thankYouUrl = `https://multas.eu?form_submitted=true`;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 transition-opacity duration-300"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
    >
      <div 
        className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
          <h2 id="contact-modal-title" className="text-2xl font-bold text-gray-800">Contacta con Nosotros</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 text-3xl p-1"
            aria-label="Cerrar modal"
          >
            &times;
          </button>
        </div>

          <form 
            action="https://formsubmit.co/form-de-multas-eu@correo.mozmail.com" 
            method="POST" 
            className="space-y-6"
          >
            {/* Formsubmit.co settings */}
            <input type="hidden" name="_next" value={thankYouUrl} />
            <input type="hidden" name="_captcha" value="false" />
            <input type="hidden" name="_subject" value={subject} />

            <div className="grid md:grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo <span className="text-red-500">*</span></label>
                <input
                  type="text" name="Nombre Completo" id="fullName"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Correo Electrónico <span className="text-red-500">*</span></label>
                <input
                  type="email" name="Email" id="email"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Teléfono (Opcional)</label>
              <input
                type="tel" name="Teléfono" id="phone"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">País <span className="text-red-500">*</span></label>
                <select
                  name="País" id="country"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
                  required
                  defaultValue=""
                >
                  <option value="" disabled>Selecciona un país...</option>
                  {euCountries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label htmlFor="preferredLanguage" className="block text-sm font-medium text-gray-700 mb-1">Idioma de Preferencia <span className="text-red-500">*</span></label>
                <select
                  name="Idioma" id="preferredLanguage"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
                  required
                  defaultValue=""
                >
                  <option value="" disabled>Selecciona un idioma...</option>
                  {preferredLanguages.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-1">Tipo de Consulta (Opcional)</label>
              <select
                name="Tipo de Consulta" id="inquiryType"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
                defaultValue=""
              >
                <option value="">Selecciona un tipo...</option>
                {inquiryTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            
            <div>
              <label htmlFor="subject-field" className="block text-sm font-medium text-gray-700 mb-1">Asunto</label>
              <input
                type="text" name="Asunto" id="subject-field"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors bg-gray-100"
                value={subject}
                readOnly
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Mensaje <span className="text-red-500">*</span></label>
              <textarea
                name="Mensaje" id="message" rows={5}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              />
            </div>

            <div className="pt-2 text-xs text-gray-500">
              Al enviar este formulario, aceptas nuestra{' '}
              <button type="button" onClick={onOpenPrivacyModal} className="text-blue-600 hover:underline">
                Política de Privacidad
              </button> y nuestra <button type="button" onClick={onOpenCookieModal} className="text-blue-600 hover:underline">
                Política de Cookies
              </button>.
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300 focus:ring-gray-400">
                Cancelar
              </Button>
              <Button type="submit" variant="primary" className="w-full sm:w-auto">
                Enviar Consulta
              </Button>
            </div>
          </form>
      </div>
    </div>
  );
};